import React, { useEffect, useState, memo, useCallback } from 'react';
import { Container, Typography, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Snackbar, Alert, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Backdrop } from '@mui/material';
import AddCategoryModal from './AddCategoryModal';
import BreadcrumbsNav from './BreadcrumbsNav';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Tooltip from '@mui/material/Tooltip';
import axios from '../api/axios';

interface CatalogItem {
  catalogId: number;
  sku: string;
  name: string;
  price: number;
  availableQuantity: number;
  pricePer: string;
  orderPer: string;
  categoryId: number;
  categoryName: string;
  weight: number;
  batchId: number;
  sequence: number;
  special?: boolean;
}

interface Category {
  id: number;
  name: string;
  sequence: number;
}

// CatalogTableRow: each row manages its own local state for editing
interface CatalogTableRowProps {
  item: CatalogItem;
  idx: number;
  items: CatalogItem[];
  categoryName: string;
  handleDelete: (id: number) => void;
  handleSaveRow: (item: CatalogItem) => void;
  moveItem: (categoryName: string, idx: number, direction: 'up' | 'down') => void;
}

const CatalogTableRow = memo(function CatalogTableRow({ item, idx, items, categoryName, handleDelete, handleSaveRow, moveItem }: CatalogTableRowProps) {
  const [editItem, setEditItem] = React.useState(item);
  React.useEffect(() => { setEditItem(item); }, [item]);
  return (
    <TableRow
      key={editItem.catalogId}
      hover
      sx={{
        transition: 'background 0.2s',
        backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f7fa',
        '&:hover': { backgroundColor: '#e3eafc' },
        height: 36
      }}
    >
      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500, color: 'primary.main', minWidth: 150 }}>
        <TextField
          value={editItem.sku}
          onChange={e => setEditItem({ ...editItem, sku: e.target.value })}
          size="small"
          sx={{ width: 150, fontSize: '0.85rem', '& input': { fontSize: '0.85rem' } }}
        />
      </TableCell>
      <TableCell sx={{ fontWeight: 500, color: 'primary.main', minWidth: 160 }}>
        <TextField
          value={editItem.name}
          onChange={e => setEditItem({ ...editItem, name: e.target.value })}
          size="small"
            sx={{ width: 160, fontSize: '0.85rem', '& input': { fontSize: '0.85rem' } }}
        />
      </TableCell>
      <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
        <TextField
          value={editItem.price}
          onChange={e => setEditItem({ ...editItem, price: Number(e.target.value) })}
          size="small"
          sx={{ width: 90, fontSize: '0.85rem', '& input': { fontSize: '0.85rem' } }}
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Select
          value={['case', 'ea'].includes((editItem.pricePer || '').toLowerCase()) ? (editItem.pricePer[0].toUpperCase() + editItem.pricePer.slice(1).toLowerCase()) : 'Ea'}
          onChange={e => setEditItem({ ...editItem, pricePer: e.target.value })}
          size="small"
            sx={{ width: 100, fontSize: '0.85rem', '& .MuiSelect-select': { fontSize: '0.85rem' } }}
        >
          <MenuItem value="Case">Case</MenuItem>
          <MenuItem value="Ea">Ea</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Select
          value={['case', 'ea'].includes((editItem.orderPer || '').toLowerCase()) ? (editItem.orderPer[0].toUpperCase() + editItem.orderPer.slice(1).toLowerCase()) : 'Ea'}
          onChange={e => setEditItem({ ...editItem, orderPer: e.target.value })}
          size="small"
            sx={{ width: 100, fontSize: '0.85rem', '& .MuiSelect-select': { fontSize: '0.85rem' } }}
        >
          <MenuItem value="Case">Case</MenuItem>
          <MenuItem value="Ea">Ea</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ minWidth: 80 }}>
        <TextField
          value={editItem.weight}
          onChange={e => setEditItem({ ...editItem, weight: Number(e.target.value) })}
          size="small"
            sx={{ width: 75, fontSize: '0.85rem', '& input': { fontSize: '0.85rem' } }}
          type="number"
          inputProps={{ min: 0, step: 1 }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 80, textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={!!editItem.special}
          onChange={e => setEditItem({ ...editItem, special: e.target.checked })}
          style={{ transform: 'scale(1.3)' }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="Delete this item">
            <Button color="error" variant="outlined" size="small" onClick={() => handleDelete(editItem.catalogId)}>
              <DeleteOutlineIcon />
            </Button>
          </Tooltip>
          <Tooltip title="Save Changes">
            <Button color="primary" variant="outlined" size="small" onClick={() => handleSaveRow(editItem)}>
              <SaveOutlinedIcon />
            </Button>
          </Tooltip>
        </Box>
      </TableCell>
      {/* Up/Down arrows for reordering - now last column */}
      <TableCell sx={{ minWidth: 60 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            size="small"
            disabled={idx === 0}
            onClick={() => moveItem(categoryName, idx, 'up')}
            sx={{ minWidth: 32, mb: 0.5 }}
          >
            <ArrowUpwardIcon fontSize="small" />
          </Button>
          <Button
            size="small"
            disabled={idx === items.length - 1}
            onClick={() => moveItem(categoryName, idx, 'down')}
            sx={{ minWidth: 32 }}
          >
            <ArrowDownwardIcon fontSize="small" />
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
});

const EditCatalog: React.FC = () => {
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  // Track next temp ID for new items
  const [nextTempId, setNextTempId] = useState(-1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editSuccess, setEditSuccess] = useState(false);
  const [editError, setEditError] = useState('');
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState<number | null>(null);
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<CatalogItem | null>(null);
  const [dontAskAgain, setDontAskAgain] = useState<boolean>(false);

  // State for Open Ordering dialog
  const [openOrderingDialog, setOpenOrderingDialog] = useState(false);
  const [orderDate, setOrderDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [taxRate, setTaxRate] = useState(0.06);
  const [shipping, setShipping] = useState(0.35);
  const [orderingError, setOrderingError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get('/catalog/current'),
      axios.get('/category'),
      axios.get('/orderBatch/current')
    ])
      .then(([catalogRes, categoryRes, batchRes]) => {
        setCatalog(catalogRes.data);
        setCategories(categoryRes.data.sort((a: Category, b: Category) => a.sequence - b.sequence));
        setCurrentBatchId(batchRes.data.batchId);
      })
      .catch(() => {
        setEditError('Failed to load initial data.');
      })
      .finally(() => setLoading(false));
  }, []);

  // Save handler for a row: only update main catalog state when Save is clicked
  const handleSaveRow = (editItem: CatalogItem) => {
    // Validation
    if (!editItem.sku.trim()) {
      setEditError('SKU cannot be empty.');
      return;
    }
    if (!editItem.name.trim()) {
      setEditError('Description cannot be empty.');
      return;
    }
    if (Number(editItem.price) <= 0) {
      setEditError('Price must be greater than 0.');
      return;
    }
    if (Number(editItem.weight) <= 0) {
      setEditError('Weight must be greater than 0.');
      return;
    }
    // Calculate sequence as row number within category
    const categoryItems = catalog.filter(i => i.categoryName === editItem.categoryName);
    const sequence = categoryItems.findIndex(i => i.catalogId === editItem.catalogId) + 1;
    if (editItem.catalogId > 0) {
      // Call CatalogController.Update for existing items
      axios.put(`/catalog/${editItem.catalogId}`, { ...editItem, sequence })
        .then(() => {
          // Update local state for the edited item only
          setCatalog(prev => prev.map(item =>
            item.catalogId === editItem.catalogId
              ? { ...editItem, sequence }
              : item
          ));
          setEditSuccess(true);
        })
        .catch(() => {
          setEditError('Failed to save changes.');
        });
    } else {
      // Call CatalogController.Insert for new items
      if (!currentBatchId) {
        setEditError('No open batch found.');
        return;
      }
      // Find the correct categoryId from the section (case-insensitive match)
      const sectionCategory = categories.find(cat => cat.name.toLowerCase() === editItem.categoryName.toLowerCase());
      // Always pass catalogId: 0 for new items so server assigns the ID
      const newItem = { ...editItem, catalogId: 0, batchId: currentBatchId, categoryId: sectionCategory ? sectionCategory.id : editItem.categoryId, sequence };
      axios.post('/catalog', newItem)
        .then((response) => {
          // Add the new item returned from the server to local state
          setCatalog(prev => {
            // Remove the temp item (with negative catalogId) and add the new one
            const filtered = prev.filter(i => i.catalogId !== editItem.catalogId);
            return [...filtered, response.data].sort((a, b) => a.categoryName.localeCompare(b.categoryName) || a.sequence - b.sequence);
          });
          setEditSuccess(true);
        })
        .catch(() => {
          setEditError('Failed to insert new item.');
        });
    }
  };

  // Delete logic with confirmation
  const handleDelete = (id: number) => {
    const item = catalog.find(i => i.catalogId === id);
    if (!item) return;
    if (dontAskAgain) {
      performDelete(id);
    } else {
      setDeleteItem(item);
      setDeleteDialogOpen(true);
    }
  };

  // Actually perform the delete
  const performDelete = (id: number) => {
    axios.delete(`/catalog/${id}`)
      .then(() => {
        setCatalog((prev) => prev.filter(item => item.catalogId !== id));
        setEditSuccess(true);
      })
      .catch(() => {
        setEditError('Failed to delete item.');
      });
    setDeleteDialogOpen(false);
    setDeleteItem(null);
  };

  const handleAddItem = (category: Category) => {
    setCatalog(prev => [
      ...prev,
      {
        catalogId: nextTempId,
        sku: '',
        name: '',
        price: 0,
        availableQuantity: 0,
        pricePer: 'Case',
        orderPer: 'Case',
        categoryId: category.id,
        categoryName: category.name,
        weight: 0,
        batchId: currentBatchId ?? 0,
        sequence: prev.filter(i => i.categoryName === category.name).length + 1 // add at end
      }
    ]);
    setNextTempId(id => id - 1);
  };

  const handleAddCategory = async (name: string) => {
    const response = await axios.post('/category', { name });
    // Add new category to local state so it renders immediately
    setCategories(prev => [...prev, response.data].sort((a, b) => a.sequence - b.sequence));
  };

  // Memoized moveItem function
  const moveItem = useCallback((categoryName: string, idx: number, direction: 'up' | 'down') => {
    setCatalog(prev => {
      const catItems = prev.filter(item => item.categoryName === categoryName);
      if (catItems.length < 2) return prev;
      const globalIdxs = catItems.map(item => prev.findIndex(i => i.catalogId === item.catalogId));
      const itemIdx = globalIdxs[idx];
      const swapIdx = direction === 'up' ? globalIdxs[idx - 1] : globalIdxs[idx + 1];
      if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === catItems.length - 1)) return prev;
      const newCatalog = [...prev];
      [newCatalog[itemIdx], newCatalog[swapIdx]] = [newCatalog[swapIdx], newCatalog[itemIdx]];
      // After reorder, update sequence for all items in this category and save to server
      const updatedCatItems = newCatalog.filter(item => item.categoryName === categoryName);
      updatedCatItems.forEach((item, i) => {
        const updatedItem = { ...item, sequence: i + 1 };
        axios.put(`/catalog/${item.catalogId}`, updatedItem);
      });
      // Also update local state with new sequence
      return newCatalog.map(item =>
        item.categoryName === categoryName
          ? { ...item, sequence: updatedCatItems.findIndex(i => i.catalogId === item.catalogId) + 1 }
          : item
      );
    });
  }, []);

  return (
    <>
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="lg" sx={{ py: 2, display: loading ? 'none' : undefined }}>
        <BreadcrumbsNav current="Edit Catalog" />
        <Typography variant="h3" gutterBottom align="center" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1, mb: 1, fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Edit Catalog
        </Typography>
        {categories.map(category => {
          const items = catalog.filter(item => item.categoryName === category.name);
          return (
            <React.Fragment key={category.id}>
              <Typography variant="h5" sx={{ mt: 4, mb: 1, color: 'primary.main', fontWeight: 700 }}>
                {category.name}
              </Typography>
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, bgcolor: 'primary.dark', mb: 2, minWidth: 950 }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 150 }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 160 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 100 }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 120 }}>Price Per</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 120 }}>Order Per</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 80 }}>Weight (lbs)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 80, textAlign: 'center' }}>Special</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 120, textAlign: 'center' }}>Actions</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: 15, color: 'primary.contrastText', minWidth: 60 }}>Reorder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => (
                      <CatalogTableRow
                        key={item.catalogId}
                        item={item}
                        idx={idx}
                        items={items}
                        categoryName={category.name}
                        handleDelete={handleDelete}
                        handleSaveRow={handleSaveRow}
                        moveItem={moveItem}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="outlined" color="primary" onClick={() => handleAddItem(category)} startIcon={<AddCircleOutlineIcon />}>
                  Add Item
                </Button>
              </Box>
            </React.Fragment>
          );
        })}
        <Box display="flex" justifyContent="flex-end" mt={4} mb={2}>
          <Button variant="contained" color="primary" onClick={() => setAddCategoryOpen(true)} startIcon={<AddCircleOutlineIcon />}>
            Add Category
          </Button>
        </Box>
        <AddCategoryModal
          open={addCategoryOpen}
          onClose={() => setAddCategoryOpen(false)}
          onAddCategory={handleAddCategory}
        />

        {/* Open Ordering Dialog */}
        <Dialog open={openOrderingDialog} onClose={() => setOpenOrderingDialog(false)}>
          <DialogTitle>Open Ordering</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Order Date"
              type="date"
              fullWidth
              value={orderDate}
              onChange={e => setOrderDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              margin="dense"
              label="Delivery Date"
              type="date"
              fullWidth
              value={deliveryDate}
              onChange={e => setDeliveryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              margin="dense"
              label="Tax Rate (%)"
              type="number"
              fullWidth
              value={taxRate * 100}
              onChange={e => setTaxRate(Number(e.target.value) / 100)}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
            <TextField
              margin="dense"
              label="Shipping Fee"
              type="number"
              fullWidth
              value={shipping}
              onChange={e => setShipping(Number(e.target.value))}
              inputProps={{ min: 0, step: 0.01 }}
            />
            {orderingError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{orderingError}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenOrderingDialog(false)} color="inherit">Cancel</Button>
            <Button
              onClick={() => {
                // Validate order details
                if (!orderDate || !deliveryDate) {
                  setOrderingError('Order and delivery dates are required.');
                  return;
                }
                if (new Date(deliveryDate) < new Date(orderDate)) {
                  setOrderingError('Delivery date cannot be earlier than order date.');
                  return;
                }
                // Create the order
                axios.post('/order', {
                  orderDate,
                  deliveryDate,
                  taxRate,
                  shipping,
                  items: catalog.filter(item => item.catalogId > 0) // Only include saved items
                })
                  .then(() => {
                    setOpenOrderingDialog(false);
                    setEditSuccess(true);
                    // Optionally, refresh catalog or show order details
                  })
                  .catch(() => {
                    setOrderingError('Failed to create order.');
                  });
              }}
              color="primary" variant="contained"
            >
              Create Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setDeleteItem(null); }}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this item?</Typography>
            <Box display="flex" alignItems="center" mt={2}>
              <input
                type="checkbox"
                id="dontAskAgain"
                checked={dontAskAgain}
                onChange={e => setDontAskAgain(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <label htmlFor="dontAskAgain">Don't ask me again</label>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDeleteDialogOpen(false); setDeleteItem(null); }} color="inherit">Cancel</Button>
            <Button onClick={() => deleteItem && performDelete(deleteItem.catalogId)} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={editSuccess} autoHideDuration={3000} onClose={() => setEditSuccess(false)}>
          <Alert onClose={() => setEditSuccess(false)} severity="success" sx={{ width: '100%' }}>
            ✅ Catalog updated successfully!
          </Alert>
        </Snackbar>
        <Snackbar open={!!editError} autoHideDuration={4000} onClose={() => setEditError('')}>
          <Alert onClose={() => setEditError('')} severity="error" sx={{ width: '100%' }}>
            {editError}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default EditCatalog;
